import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Loader2, FolderCog } from 'lucide-react';
import { toast } from 'sonner';
import { ProductForm } from './ProductForm';
import { ProductTable } from './ProductTable';
import { CategoryManager } from './CategoryManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyProduct = {
  name: '', category: 'bow-ties', price: '', description: '', image: '',
  material: 'Premium Silk', colors: '', isNew: false, isBestSeller: false, stock: '25',
};

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [initialForm, setInitialForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const loadProducts = useCallback(() => {
    fetch(`${API_URL}/api/products`)
      .then((r) => r.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const openAdd = () => {
    setInitialForm(emptyProduct);
    setEditing('new');
  };

  const openEdit = (p) => {
    setInitialForm({ ...p, price: String(p.price), colors: (p.colors || []).join(', '), stock: String(p.stock ?? 25) });
    setEditing(p.id);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const res = await fetch(
        editing === 'new' ? `${API_URL}/api/products` : `${API_URL}/api/products/${editing}`,
        {
          method: editing === 'new' ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error();
      toast.success(editing === 'new' ? 'Product added' : 'Product updated');
      setEditing(null);
      loadProducts();
    } catch {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/products/${p.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Product deleted');
      loadProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#7B1E2B]" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-600">{products.length} products</p>
        <div className="flex gap-2">
          <button
            data-testid="manage-categories-button"
            onClick={() => setShowCategories(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#001B2A] text-white rounded-md font-semibold text-sm hover:bg-[#7B1E2B] transition-colors"
          >
            <FolderCog className="h-4 w-4" /> Manage Categories
          </button>
          <button
            data-testid="add-product-button"
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-[#7B1E2B] text-white rounded-md font-semibold text-sm hover:bg-[#001B2A] transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Product
          </button>
        </div>
      </div>

      <ProductTable products={products} onEdit={openEdit} onDelete={handleDelete} />

      {showCategories && (
        <CategoryManager onClose={() => setShowCategories(false)} onChanged={loadProducts} />
      )}

      {editing && (
        <ProductForm
          key={editing}
          initial={initialForm}
          isNew={editing === 'new'}
          saving={saving}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};
